import { defineComponent as _defineComponent } from 'vue'
import { BackButton } from '@/modules/common/components/buttons';
import { ShowMoneyToggleButton } from '@/modules/common/components/buttons/ShowMoneyToggleButton';
import { HeroContainer } from '@/modules/common/components/Hero';
import { BffInvestments } from '@/modules/wallet-builder/src/services/bff-investments';
import { IPortfolioDistributionResponse } from '@/modules/wallet-builder/src/services/types/models/IBffInvestments';
import {
  getPortfolioHero,
  IPortfolioHero
} from '@/modules/wealth/services/portfolio';
import { PortfolioModule } from '@/store/modules/PortfolioModule';
import {
  PortfolioCategories,
  SuggestionPortfolioAllocationType
} from '@/types/models/Portfolio';
import {
  ContextColorProvider,
  LoaderIndicator,
  NavBar,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs
} from '@warrenbrasil/nebraska-web';
import { computed, onMounted, provide, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import {
  About,
  Allocation,
  Orders,
  Performance,
  PortfolioHero
} from './components';
import { WealthPortfolioTabsIndexes, WealthPortfolioTabs } from './types';


export default /*#__PURE__*/_defineComponent({
  __name: 'WealthPortfolioV2',
  setup(__props) {

const route = useRoute();
const router = useRouter();

const bffInvestments = new BffInvestments();

const portfolioHero = ref<IPortfolioHero | null>(null);
const isPortfolioHeroLoading = ref(true);
const hasPortfolioHeroError = ref(true);

const isPortfolioDistributionLoading = ref(true);
const hasPortfolioDistributionError = ref(false);
const portfolioDistribution = ref<IPortfolioDistributionResponse | null>(null);

const reloadCause = ref('');
const currentTabIndex = ref(WealthPortfolioTabsIndexes.Performance);
const tabOpenOrders = ref(0);
const tabOpenOrdersCount = computed(() => {
  if (tabOpenOrders.value === 0) return '';

  return tabOpenOrders.value >= 9 ? '(+9)' : `(${tabOpenOrders.value})`;
});

const isFixedIncomePortfolioType = computed(() => {
  return (
    portfolioHero.value?.allocationType ===
    SuggestionPortfolioAllocationType.FIXED_INCOME
  );
});

const portfolioId = computed(() => {
  const { id } = route.params;

  return id;
});

const portfolioType = computed(() => {
  const { type } = route.params;

  return type;
});

const hasPositions = computed(() => {
  return portfolioHero.value?.hasPositions || false;
});

const isCustom = computed(() => {
  return portfolioType.value === PortfolioCategories.Custom;
});

async function fetchPortfolioDistribution() {
  isPortfolioDistributionLoading.value = true;
  hasPortfolioDistributionError.value = false;

  try {
    const portfolio = await bffInvestments.getPortfolioDistribution(
      portfolioId.value
    );
    portfolioDistribution.value = portfolio;
  } catch (error) {
    hasPortfolioDistributionError.value = true;
  } finally {
    isPortfolioDistributionLoading.value = false;
  }
}

async function fetchPortfolioHero() {
  isPortfolioHeroLoading.value = true;
  hasPortfolioHeroError.value = false;

  try {
    portfolioHero.value = await getPortfolioHero(portfolioId.value);
    tabOpenOrders.value = portfolioHero.value.openedOrders;
  } catch (e) {
    hasPortfolioHeroError.value = true;
  } finally {
    isPortfolioHeroLoading.value = false;
  }
}

async function fetchPortfolioDetails(reloadCauseParam?: string) {
  reloadCauseParam && (reloadCause.value = reloadCauseParam);
  await Promise.all([fetchPortfolioDistribution(), fetchPortfolioHero()]);
}

async function validatePortfolio() {
  try {
    if (
      !PortfolioModule.getPortfolioById(portfolioId.value) ||
      !PortfolioModule.portfolios.length
    ) {
      await PortfolioModule.fetchPortfolios();
    }

    const portfolio = PortfolioModule.getPortfolioById(portfolioId.value);

    if (!portfolio) {
      router.push({ name: 'wealth' });
      return;
    }

    if (!portfolioType.value || portfolioType.value !== portfolio.type) {
      router.replace({
        name: 'wealthPortfolio',
        params: { type: portfolio!.type, id: portfolioId.value }
      });
    }
  } catch (error) {
    router.push({ name: 'wealth' });
  }
}

function onChangeTab(selectedTabIndex: WealthPortfolioTabsIndexes) {
  currentTabIndex.value = selectedTabIndex;
}

function handleGoBackClick() {
  router.push({ name: 'wealth' });
}

onMounted(async () => {
  const tabParamsMapper: Record<
    WealthPortfolioTabs,
    WealthPortfolioTabsIndexes
  > = {
    [WealthPortfolioTabs.Performance]: WealthPortfolioTabsIndexes.Performance,
    [WealthPortfolioTabs.Allocation]: WealthPortfolioTabsIndexes.Allocation,
    [WealthPortfolioTabs.Orders]: WealthPortfolioTabsIndexes.Orders,
    [WealthPortfolioTabs.About]: WealthPortfolioTabsIndexes.About
  };

  const tab = route.query?.tab as WealthPortfolioTabs;

  currentTabIndex.value =
    tabParamsMapper[tab] || WealthPortfolioTabsIndexes.Performance;

  await validatePortfolio();

  await fetchPortfolioDetails();
});

provide('portfolioDistribution', portfolioDistribution);
provide('isPortfolioDistributionLoading', isPortfolioDistributionLoading);
provide('reloadCause', reloadCause);
provide('currentTabIndex', currentTabIndex);

return { __sfc: true,route, router, bffInvestments, portfolioHero, isPortfolioHeroLoading, hasPortfolioHeroError, isPortfolioDistributionLoading, hasPortfolioDistributionError, portfolioDistribution, reloadCause, currentTabIndex, tabOpenOrders, tabOpenOrdersCount, isFixedIncomePortfolioType, portfolioId, portfolioType, hasPositions, isCustom, fetchPortfolioDistribution, fetchPortfolioHero, fetchPortfolioDetails, validatePortfolio, onChangeTab, handleGoBackClick, BackButton, ShowMoneyToggleButton, HeroContainer, ContextColorProvider, LoaderIndicator, NavBar, Tab, TabList, TabPanel, TabPanels, Tabs, About, Allocation, Orders, Performance, PortfolioHero }
}

})